import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthUser, authApi } from '@/api/auth'
import { registerAuthContextProvider } from '@/api/client'
import { clearPersistedQueryCache } from '@/lib/queryCache'

export interface PreviewTemplate {
  id: number
  name: string
  permissions: string[]   // ['finops:read', 'chamados:read', ...]
  tenants: { id: number; name: string; environment: string }[]
  /** Caps do perfil por escopo. Sem isto o preview mostrava a base cheia de um
   *  perfil restrito — mentia na direção permissiva. */
  caps?: { scope_kind: 'environment' | 'tenant'; scope_value: string; permissions: string[] }[]
}

interface AuthState {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isRestoring: boolean
  selectedTenantId: number | null
  selectedOrgId: number | null
  globalScope: boolean
  previewTemplate: PreviewTemplate | null
  mustChangePassword: boolean
  logout: () => Promise<void>
  restoreSession: () => Promise<boolean>
  refreshUser: () => Promise<void>
  setSelectedTenant: (id: number) => void
  setSelectedOrg: (id: number | null) => void
  setGlobalScope: (v: boolean) => void
  enterPreview: (template: PreviewTemplate) => void
  exitPreview: () => void
}

// Verifica sincronamente se há token salvo para inicializar isRestoring corretamente.
// Evita flash de redirect para /login enquanto restoreSession ainda não rodou.
const _hasPersistedToken = typeof window !== 'undefined'
  ? !!localStorage.getItem('session_token')
  : false

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isRestoring: _hasPersistedToken,
      selectedTenantId: null,
      selectedOrgId: null,
      globalScope: false,
      previewTemplate: null,
      mustChangePassword: false,

      logout: async () => {
        try { await authApi.logout() } catch { /* ignore */ }
        localStorage.removeItem('session_token')
        // M-06 (TASK-2830): limpa o cache do react-query (memória + snapshot
        // persistido no localStorage) — não reter PII após logout.
        clearPersistedQueryCache()
        // Limpa o store local de busca de mensagens (plaintext no IndexedDB) —
        // dispositivo compartilhado não pode reter texto após logout.
        // Best-effort (dynamic import + catch); nunca faz o logout falhar.
        void import('@/lib/messageStore')
          .then((m) => m.messageStore.clear())
          .catch(() => {})
        set({ token: null, user: null, isAuthenticated: false, selectedTenantId: null, selectedOrgId: null, globalScope: false, mustChangePassword: false })
      },

      restoreSession: async () => {
        const token = localStorage.getItem('session_token')
        if (!token) {
          set({ isRestoring: false })
          return false
        }
        try {
          const user = await authApi.me()
          if (user.must_change_password) {
            set({ token, user, isAuthenticated: true, isRestoring: false, mustChangePassword: true })
            return true
          }
          const current = get().selectedTenantId
          const validId = user.tenants?.some(t => t.id === current)
            ? current
            : user.tenants?.[0]?.id ?? null
          set({ token, user, isAuthenticated: true, isRestoring: false, selectedTenantId: validId, mustChangePassword: false })
          return true
        } catch (err: any) {
          // Apenas invalida a sessao em caso de 401 (token expirado/invalido).
          // Erros temporarios (rede, 5xx) nao devem remover o token nem fazer logout.
          const status = err?.response?.status
          if (status === 401 || status === 403) {
            localStorage.removeItem('session_token')
            set({ token: null, user: null, isAuthenticated: false, isRestoring: false })
          } else {
            set({ isRestoring: false })
          }
          return false
        }
      },

      refreshUser: async () => {
        // Re-fetch /api/auth/me para atualizar tenants, roles e permissoes.
        // Usado apos mutacoes que alteram permissoes/roles do proprio user logado —
        // senao o seletor de tenant/RBAC fica com dados stale ate F5/relogin.
        try {
          const user = await authApi.me()
          const current = get().selectedTenantId
          const validId = user.tenants?.some(t => t.id === current)
            ? current
            : user.tenants?.[0]?.id ?? null
          // mustChangePassword acompanha o /me. Sem isto a flag ficava presa em
          // `true` depois da troca bem-sucedida e o SetupWizard (que deriva o
          // passo dela) voltava ao MESMO passo de senha — cada nova tentativa
          // batia em 400 `password_change_not_required`. Incidente 21/08/2026.
          set({ user, selectedTenantId: validId, mustChangePassword: !!user.must_change_password })
        } catch {
          // Falha transient e aceitavel (ex.: 5xx) — user continua com dado anterior
        }
      },

      setSelectedTenant: (id) => set({ selectedTenantId: id }),

      // Org de contexto (per-org-template §3). null = sem org → união no backend.
      // Escolher uma org concreta limpa o escopo global (são mutuamente exclusivos).
      setSelectedOrg: (id) => set({ selectedOrgId: id, ...(id != null ? { globalScope: false } : {}) }),

      // Escopo global "Todas as organizações (plataforma)". Ativar limpa selectedOrgId.
      setGlobalScope: (v) => set(v ? { globalScope: true, selectedOrgId: null } : { globalScope: false }),

      enterPreview: (template) => set({ previewTemplate: template }),
      exitPreview: () => set({ previewTemplate: null }),
    }),
    {
      name: 'consoleai-auth',
      partialize: (state) => ({ token: state.token, selectedTenantId: state.selectedTenantId, selectedOrgId: state.selectedOrgId, globalScope: state.globalScope }),
    },
  ),
)

// Register auth context so axios interceptor can inject user/tenant/org headers
registerAuthContextProvider(() => {
  const { user, selectedTenantId, selectedOrgId } = useAuthStore.getState()
  return {
    userId: user?.id,
    tenantId: selectedTenantId ?? undefined,
    orgId: selectedOrgId ?? undefined,
  }
})

export function useCurrentTenant() {
  const { user, selectedTenantId } = useAuthStore()
  return user?.tenants?.find(t => t.id === selectedTenantId) ?? user?.tenants?.[0] ?? null
}

/**
 * Lista de tenants aos quais o usuario logado tem acesso (vinda de
 * /auth/me). Usado em filtros globais (ex.: ZtnaCustomers v2) onde o
 * recurso e global mas pode ser filtrado por tenant.
 *
 * Retorna array vazio se nao houver user/tenants.
 */
export type UserTenant = NonNullable<AuthUser['tenants']>[number]

export function useUserTenants(): UserTenant[] {
  const user = useAuthStore(s => s.user)
  return user?.tenants ?? []
}

/**
 * Decisão de UI, espelhando `has_permission` (`rbac.py:1513-1664`).
 *
 * Função pura e exportada para poder ser testada sem hook — o hook `useCan`
 * abaixo é um wrapper que só lê o store.
 *
 * DUAS precedências, e a diferença não é detalhe:
 *
 * - **Com tenant** — superusuário, depois `global_read_all` para leitura, depois
 *   o cap do tenant NEGA, e só então `tenant_permissions[tid]`.
 *   `global_permissions` NÃO participa quando o usuário tem template aplicado:
 *   ela contém a base do template copiada para o escopo global
 *   (`get_user_all_permissions`), e consultá-la aqui era o que fazia o cap não
 *   chegar à tela e um tenant FORA do escopo do perfil ser liberado — o usuário
 *   via o botão e tomava 403.
 * - **Sem tenant** — `global_permissions` é a fonte correta: com `tenant_id=None`
 *   o backend concede a base inteira do template (`rbac.py:1609`) e caps não se
 *   aplicam. É disto que `RequirePermission ... global` depende para `/admin/*`.
 *
 * A EXCEÇÃO que uma regra ingênua quebraria: usuário SEM template aplicado. Para
 * ele o backend aceita concessão com `utp.tenant_id IS NULL` numa pergunta COM
 * tenant (`rbac.py:1647`), e `get_user_all_permissions` NÃO copia essas
 * concessões para `by_tenant` (`rbac.py:1353-1363`). Sem o
 * `global_applies_to_tenants` do `/auth/me`, negaríamos onde o backend concede,
 * para todo usuário cujo acesso vem só de concessão global.
 */
export function buildCan(
  user: AuthUser | null,
  selectedTenantId: number | null,
  previewTemplate: PreviewTemplate | null,
) {
  return (resource: string, action: string, tenantId?: number | null): boolean => {
    if (!user) return false
    const perm = `${resource}:${action}`

    // Modo preview: usa somente as permissions do template, JÁ com o cap do
    // tenant selecionado. Precedência tenant > ambiente > base (igual ao
    // resolve_template_cap do backend).
    if (previewTemplate) {
      if (!previewTemplate.permissions.includes(perm)) return false
      const tid = tenantId ?? selectedTenantId
      const tenant = previewTemplate.tenants.find((t) => t.id === tid)
      const cap =
        previewTemplate.caps?.find(
          (c) => c.scope_kind === 'tenant' && c.scope_value === String(tid),
        )
        ?? (tenant
          ? previewTemplate.caps?.find(
              (c) => c.scope_kind === 'environment' && c.scope_value === tenant.environment,
            )
          : undefined)
      return cap ? cap.permissions.includes(perm) : true
    }

    // Superusuário → acesso total. `is_superuser` cobre a flag `is_admin` E a
    // função `admin`, que `has_permission` honra (rbac.py:1546) e a UI ignorava.
    // `is_admin` continua sendo lido para o payload de antes do deploy.
    if (user.is_superuser || user.is_admin) return true

    const globals = user.global_permissions ?? []
    if (globals.includes('admin:full')) return true

    const tid = tenantId ?? selectedTenantId
    if (tid == null) {
      // Pergunta de plataforma: a lista global responde.
      return globals.includes(perm)
    }

    // plataforma:global_view concede LEITURA em qualquer tenant, e vem ANTES do
    // cap (rbac.py:1557 checa global_view antes dos ramos que aplicam cap).
    if (user.global_read_all && action === 'read') return true

    if ((user.capped_tenants?.[String(tid)] ?? []).includes(perm)) return false

    if ((user.tenant_permissions?.[String(tid)] ?? []).includes(perm)) return true

    // Sem template aplicado, a concessão global vale para qualquer tenant.
    return Boolean(user.global_applies_to_tenants) && globals.includes(perm)
  }
}

/**
 * "Tem esta permissao em ALGUM tenant?" — o gate de tela que nao depende do
 * seletor (frota do robo, TASK-11262). Pergunta o mesmo `can` tenant a tenant,
 * sobre a uniao dos tenants do usuario e dos que tem permissao explicita; nao
 * reimplementa precedencia nenhuma.
 */
export function podeEmAlgumTenant(
  user: AuthUser | null,
  can: (resource: string, action: string, tenantId?: number | null) => boolean,
  resource: string,
  action: string,
): boolean {
  if (!user) return false
  if (can(resource, action)) return true
  const ids = new Set<number>((user.tenants ?? []).map((t) => t.id))
  Object.keys(user.tenant_permissions ?? {}).forEach((k) => ids.add(Number(k)))
  for (const id of ids) {
    if (Number.isFinite(id) && can(resource, action, id)) return true
  }
  return false
}

/**
 * "Tem esta permissao na PLATAFORMA?" — a pergunta que o backend faz com
 * `has_permission(conn, user_id, None, recurso, acao)`: superusuario,
 * `admin:full` ou a permissao na lista global. A concessao num tenant NAO
 * conta, e o seletor do topo nao entra. E' o gate da frota do robo desde que
 * ela passou a ser da plataforma (spec 2026-09-24-robo-sem-cliente, D1).
 *
 * Nao reusa `buildCan(..., tenantId=null)`: la' `null` cai no tenant
 * selecionado, e a resposta passaria a depender do seletor.
 */
export function podeNaPlataforma(user: AuthUser | null, resource: string, action: string): boolean {
  if (!user) return false
  if (user.is_superuser || user.is_admin) return true
  const globals = user.global_permissions ?? []
  return globals.includes('admin:full') || globals.includes(`${resource}:${action}`)
}

export function useCanNaPlataforma() {
  const user = useAuthStore((s) => s.user)
  return (resource: string, action: string) => podeNaPlataforma(user, resource, action)
}

export function useCan() {
  const { user, selectedTenantId, previewTemplate } = useAuthStore()
  return buildCan(user, selectedTenantId, previewTemplate)
}
